import { WORKSPACE_LIMITS } from "../../../constant/workspaceLimits";
import { useRef, useCallback } from "react";
import { toast } from "react-toastify";
import JSZip from "jszip";
import { MAX_PROJECT_SIZE_BYTES, formatBytes } from "../services/projectService";

/**
 * Hook to manage local project file imports (.zip, .py, .js, .sb3 via FileReader/JSZip)
 * and file downloads (multi-file .zip archives, single blobs, and Scratch SB3 iframe messages).
 */
export const useWorkspaceFileIO = ({
  activeWorkspaceEnv,
  setActiveWorkspaceEnv,
  activeWorkspaceCode,
  activeWorkspaceFiles,
  projectName,
  setProjectName,
  setProjectId,
  setLoadedProjectData,
  setActiveWorkspaceCode,
  setActiveWorkspaceFiles,
  setLastSavedCloudCode,
  setBaselineCode,
  setWorkspaceKey,
  isDirty,
  openDialog,
  onClearDraft,
  selectedChild = null,
}) => {
  const fileInputRef = useRef(null);

  const isJuniorWorkspace =
    activeWorkspaceEnv === "scratch_jr" ||
    activeWorkspaceEnv === "junior" ||
    activeWorkspaceEnv === "junior_blocks_basic" ||
    activeWorkspaceEnv === "junior_blocks_adv";

  const currentJuniorSubMode =
    activeWorkspaceEnv === "junior_blocks_adv"
      ? "advanced"
      : activeWorkspaceEnv === "junior_blocks_basic"
      ? "basic"
      : selectedChild?.juniorSubMode ||
        (selectedChild?.grade === "Class 1" ? "basic" : "advanced");

  const isScratchWorkspace = activeWorkspaceEnv === "scratch";


  const executeDownloadLocal = useCallback(
    async (finalName) => {
      const nameToUse = (finalName || projectName || "project").trim();
      setProjectName(nameToUse);

      const isJuniorEnv =
        activeWorkspaceEnv === "scratch_jr" ||
        activeWorkspaceEnv === "junior" ||
        activeWorkspaceEnv === "junior_blocks_basic" ||
        activeWorkspaceEnv === "junior_blocks_adv";

      const isBlockWorkspace = activeWorkspaceEnv === "scratch" || isJuniorEnv;

      if (isBlockWorkspace) {
        // Send message to Scratch or Junior iframe to trigger native SB3 download
        const iframe = document.querySelector(
          "iframe[title='Practido Visual Block Studio'], iframe[title='Junior Blocks'], iframe[title='Practido Junior Studio'], iframe[src*='junior.html'], iframe[src*='index.html']"
        );
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage(
            { type: "DOWNLOAD_SB3", payload: { projectName: nameToUse } },
            "*"
          );
        }
        return;
      }

      try {
        const zip = new JSZip();
        const safeBaseName = nameToUse.replace(/[^a-z0-9_-]/gi, "_").toLowerCase() || "project";

        // Multi-file packing via JSZip
        if (Array.isArray(activeWorkspaceFiles) && activeWorkspaceFiles.length > 0) {
          activeWorkspaceFiles.forEach((file) => {
            const filePath = file.path || file.name;
            if (file.type === "folder") {
              zip.folder(filePath);
            } else {
              zip.file(filePath, file.content ?? "");
            }
          });
        } else if (activeWorkspaceCode) {
          const entryName = activeWorkspaceEnv === "javascript" ? "main.js" : "main.py";
          zip.file(entryName, typeof activeWorkspaceCode === "string" ? activeWorkspaceCode : JSON.stringify(activeWorkspaceCode));
        }

        const zipBlob = await zip.generateAsync({ type: "blob" });
        const filename = `${safeBaseName}.zip`;

        const url = URL.createObjectURL(zipBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast.success(`Downloaded project as "${filename}"`);
      } catch (err) {
        console.error("Failed to package .zip project:", err);
        toast.error("Failed to generate project .zip file.");
      }
    },
    [activeWorkspaceCode, activeWorkspaceFiles, activeWorkspaceEnv, projectName, setProjectName]
  );

  const handleDownloadClick = useCallback(() => {
    if (
      projectName.trim().toLowerCase() === "unnamed project" ||
      projectName.trim() === ""
    ) {
      openDialog({
        type: "RENAME",
        title: "Name Your Project",
        message: "",
        confirmText: "Download Project",
        confirmClass: "bg-purple-600 hover:bg-purple-500 text-white",
        cancelText: "Cancel",
        showInput: true,
        inputValue: projectName === "Unnamed Project" ? "" : projectName,
        payload: "download",
      });
    } else {
      executeDownloadLocal(projectName);
    }
  }, [projectName, openDialog, executeDownloadLocal]);

  const handleUploadLocalClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
      fileInputRef.current.click();
    }
  }, []);

  const executeLoadLocalProject = useCallback(
    (localProject) => {
      if (!localProject) return;
      const { projectName: name, environment: targetEnv, code, files } = localProject;

      if (targetEnv && targetEnv !== activeWorkspaceEnv && setActiveWorkspaceEnv) {
        setActiveWorkspaceEnv(targetEnv);
      }

      setProjectName(name || "Unnamed Project");
      setProjectId(null); // Local import is treated as new cloud project

      if (Array.isArray(files) && files.length > 0) {
        if (setActiveWorkspaceFiles) {
          setActiveWorkspaceFiles(files);
        }
        setLoadedProjectData({ code, files });
      } else {
        if (setActiveWorkspaceFiles) {
          setActiveWorkspaceFiles(null);
        }
        setLoadedProjectData(code);
      }

      setActiveWorkspaceCode(code);
      setLastSavedCloudCode(null);
      setBaselineCode(code);

      if (onClearDraft) {
        onClearDraft();
      }

      // Force remount of modular workspace to trigger initialData load
      setWorkspaceKey((prev) => prev + 1);
      toast.success(`Opened "${name}" successfully`);
    },
    [
      activeWorkspaceEnv,
      setActiveWorkspaceEnv,
      setProjectName,
      setProjectId,
      setLoadedProjectData,
      setActiveWorkspaceCode,
      setActiveWorkspaceFiles,
      setLastSavedCloudCode,
      setBaselineCode,
      setWorkspaceKey,
      onClearDraft,
    ]
  );

  const handleFileChange = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const MAX_LOCAL_IMPORT_SIZE_BYTES = WORKSPACE_LIMITS.LOCAL_FILE_IMPORT_LIMIT_BYTES; // 500 KB limit for local computer files
      if (file.size > MAX_LOCAL_IMPORT_SIZE_BYTES) {
        toast.error(
          `File "${file.name}" (${formatBytes(file.size)}) exceeds the maximum allowed import limit of ${formatBytes(MAX_LOCAL_IMPORT_SIZE_BYTES)}. Please choose a smaller file.`
        );
        e.target.value = "";
        return;
      }

      const fileName = file.name;
      let newName = fileName.split(".").slice(0, -1).join(".") || fileName;

      const getEnvDisplayName = (env) => {
        if (env === "python") return "Python";
        if (env === "javascript") return "JavaScript";
        if (env === "scratch") return "Scratch Visual Blocks";
        if (env === "junior_blocks_basic") return "Junior Blocks (Basic)";
        if (env === "junior_blocks_adv") return "Junior Blocks (Advanced)";
        if (env === "scratch_jr" || env === "junior") return "Junior Blocks";
        return env ? env.toUpperCase() : "Workspace";
      };

      const promptOrLoad = (localProject, targetEnv, displayTargetName) => {
        const currentEnvName = getEnvDisplayName(activeWorkspaceEnv);
        if (targetEnv === activeWorkspaceEnv) {
          const hasContent =
            typeof activeWorkspaceCode === "string"
              ? activeWorkspaceCode.trim().length > 0
              : !!activeWorkspaceCode;

          if (isDirty || hasContent) {
            openDialog({
              type: "LOAD_LOCAL_PROJECT",
              title: `Open "${file.name}"?`,
              message: `Opening this local file will replace your current workspace content. Any unsaved changes will be cleared. Are you sure you want to proceed?`,
              confirmText: "Open File",
              confirmClass: "bg-purple-600 hover:bg-purple-500 text-white",
              cancelText: "Cancel",
              showInput: false,
              payload: localProject,
            });
          } else {
            executeLoadLocalProject(localProject);
          }
        } else {
          openDialog({
            type: "INCOMPATIBLE_PROJECT",
            title: "Incompatible Workspace",
            message: `This workspace is configured for ${currentEnvName}. Files for ${displayTargetName} cannot be opened here.`,
            confirmText: "Understood",
            confirmClass: "bg-purple-600 hover:bg-purple-500 text-white",
            cancelText: null,
            showInput: false,
            payload: null,
          });
        }
      };

      try {
        // CASE 1: .zip archive (Multi-File Practido Project)
        if (fileName.endsWith(".zip")) {
          const zip = await JSZip.loadAsync(file);
          const extractedFiles = [];
          let hasPy = false;
          let hasJs = false;

          const entries = Object.keys(zip.files);
          for (let i = 0; i < entries.length; i++) {
            const relPath = entries[i];
            const zipEntry = zip.files[relPath];

            // Ignore macOS metadata & system files
            if (relPath.startsWith("__MACOSX/") || relPath.includes(".DS_Store")) {
              continue;
            }

            if (zipEntry.dir) {
              // Folder entry
              const folderName = relPath.replace(/\/$/, "").split("/").pop();
              if (folderName) {
                extractedFiles.push({
                  id: `dir_${Date.now()}_${i}`,
                  name: folderName,
                  type: "folder",
                  parentId: null,
                  path: relPath.replace(/\/$/, ""),
                  content: "",
                  isEntry: false,
                });
              }
            } else {
              // File entry
              const textContent = await zipEntry.async("text");
              const leafName = relPath.split("/").pop();

              if (leafName.endsWith(".py")) hasPy = true;
              if (leafName.endsWith(".js")) hasJs = true;

              extractedFiles.push({
                id: `file_${Date.now()}_${i}`,
                name: leafName,
                type: "file",
                parentId: null,
                path: relPath,
                content: textContent,
                isEntry: false,
              });
            }
          }

          if (extractedFiles.length === 0) {
            toast.error("The selected .zip archive contains no readable project files.");
            e.target.value = null;
            return;
          }

          // Determine environment from files
          let detectedEnv = activeWorkspaceEnv;
          if (hasPy && !hasJs) detectedEnv = "python";
          else if (hasJs && !hasPy) detectedEnv = "javascript";
          else if (hasPy) detectedEnv = "python";

          // Designate entry file (main.py or main.js or first code file)
          const targetEntryName = detectedEnv === "javascript" ? "main.js" : "main.py";
          let entryFile = extractedFiles.find(
            (f) => f.type === "file" && f.name.toLowerCase() === targetEntryName
          );
          if (!entryFile) {
            entryFile = extractedFiles.find(
              (f) => f.type === "file" && (f.name.endsWith(".py") || f.name.endsWith(".js"))
            ) || extractedFiles.find((f) => f.type === "file");
          }

          if (entryFile) {
            entryFile.isEntry = true;
          }

          const localProject = {
            projectName: newName,
            environment: detectedEnv,
            files: extractedFiles,
            code: entryFile?.content || "",
          };

          promptOrLoad(localProject, detectedEnv, getEnvDisplayName(detectedEnv));
        }
        // CASE 2: Single .py or .js file
        else if (fileName.endsWith(".py") || fileName.endsWith(".js")) {
          const fileEnv = fileName.endsWith(".js") ? "javascript" : "python";
          const reader = new FileReader();
          reader.onload = (event) => {
            const fileContent = event.target.result || "";
            const defaultEntryName = fileEnv === "javascript" ? "main.js" : "main.py";
            const isEntry = fileName.toLowerCase() === defaultEntryName;

            const singleFile = {
              id: "main",
              name: fileName,
              type: "file",
              parentId: null,
              path: fileName,
              content: String(fileContent),
              isEntry: isEntry || true,
            };

            const localProject = {
              projectName: newName,
              environment: fileEnv,
              code: String(fileContent),
              files: [singleFile],
            };

            promptOrLoad(localProject, fileEnv, getEnvDisplayName(fileEnv));
          };
          reader.readAsText(file);
        }
        // CASE 3: Scratch / Junior Blocks .sb3 project
        else if (fileName.endsWith(".sb3")) {
          const arrayBuffer = await file.arrayBuffer();
          let detectedEnv = "scratch"; // default fallback for external / legacy MIT Scratch projects
          let detectedSubMode = "standard";

          try {
            const zip = await JSZip.loadAsync(arrayBuffer);

            // Check 1: Practido manifest
            if (zip.file("practido-workspace.json")) {
              const manifestStr = await zip.file("practido-workspace.json").async("text");
              const manifest = JSON.parse(manifestStr);
              if (manifest.environment === "junior_blocks" || manifest.environment?.includes("junior")) {
                detectedEnv = "junior_blocks";
                detectedSubMode = manifest.subMode === "advanced" ? "advanced" : "basic";
              } else if (manifest.environment === "scratch") {
                detectedEnv = "scratch";
                detectedSubMode = "standard";
              }
            }
            // Check 2: project.json meta tags & block heuristics
            else if (zip.file("project.json")) {
              const pJsonStr = await zip.file("project.json").async("text");
              const pJson = JSON.parse(pJsonStr);
              const practidoEnv = pJson.meta?.practidoEnv;
              const practidoSubMode = pJson.meta?.practidoSubMode;

              if (practidoEnv === "junior_blocks_adv" || practidoSubMode === "advanced") {
                detectedEnv = "junior_blocks";
                detectedSubMode = "advanced";
              } else if (practidoEnv === "junior_blocks_basic" || practidoSubMode === "basic") {
                detectedEnv = "junior_blocks";
                detectedSubMode = "basic";
              } else if (practidoEnv === "scratch") {
                detectedEnv = "scratch";
                detectedSubMode = "standard";
              } else {
                // Heuristic inspection of targets/blocks
                const targetsStr = JSON.stringify(pJson.targets || []);
                if (
                  targetsStr.includes("junior_") ||
                  targetsStr.includes("motion_step") ||
                  targetsStr.includes("control_repeat_junior")
                ) {
                  detectedEnv = "junior_blocks";
                  const hasAdvBlocks =
                    targetsStr.includes("motion_goto_xy") ||
                    targetsStr.includes("operator_") ||
                    targetsStr.includes("data_") ||
                    targetsStr.includes("control_if_else");
                  detectedSubMode = hasAdvBlocks ? "advanced" : "basic";
                } else {
                  detectedEnv = "scratch";
                  detectedSubMode = "standard";
                }
              }
            }
          } catch (zipErr) {
            console.warn("Could not read SB3 archive as zip:", zipErr);
          }

          // Incompatibility Rule 1: Standard Scratch project into Junior Blocks (Basic or Advanced)
          if (isJuniorWorkspace && detectedEnv === "scratch") {
            openDialog({
              type: "INCOMPATIBLE_PROJECT",
              title: "Incompatible Project",
              message:
                "This file is a standard Scratch project. It cannot be opened in Junior Blocks because it contains blocks and features not supported in the junior environment.",
              confirmText: "Understood",
              confirmClass: "bg-purple-600 hover:bg-purple-500 text-white",
              cancelText: null,
              showInput: false,
              payload: null,
            });
            e.target.value = null;
            return;
          }

          // Incompatibility Rule 2: Junior Blocks project into Standard Scratch
          if (isScratchWorkspace && detectedEnv === "junior_blocks") {
            openDialog({
              type: "INCOMPATIBLE_PROJECT",
              title: "Incompatible Project",
              message:
                "This file is a Junior Blocks project. It cannot be opened in Standard Scratch workspace because it is designed for the junior blocks environment.",
              confirmText: "Understood",
              confirmClass: "bg-purple-600 hover:bg-purple-500 text-white",
              cancelText: null,
              showInput: false,
              payload: null,
            });
            e.target.value = null;
            return;
          }

          // Incompatibility Rule 3: Junior Advanced project into Junior Basic
          if (
            isJuniorWorkspace &&
            currentJuniorSubMode === "basic" &&
            detectedEnv === "junior_blocks" &&
            detectedSubMode === "advanced"
          ) {
            openDialog({
              type: "INCOMPATIBLE_PROJECT",
              title: "Incompatible Project",
              message:
                "This project was created in Junior Advanced and cannot be opened in Junior Basic because it contains blocks and features not supported in the basic version.",
              confirmText: "Understood",
              confirmClass: "bg-amber-600 hover:bg-amber-500 text-white",
              cancelText: null,
              showInput: false,
              payload: null,
            });
            e.target.value = null;
            return;
          }

          // Compatibility Rule 4: Junior Basic project into Junior Advanced
          if (
            isJuniorWorkspace &&
            currentJuniorSubMode === "advanced" &&
            detectedEnv === "junior_blocks" &&
            detectedSubMode === "basic"
          ) {
            const localProject = {
              projectName: newName,
              environment: activeWorkspaceEnv,
              code: arrayBuffer,
            };
            const hasContent =
              typeof activeWorkspaceCode === "string"
                ? activeWorkspaceCode.trim().length > 0
                : !!activeWorkspaceCode;

            const baseMessage =
              "This project was created in Junior Basic. Opening it in the advanced version will allow you to continue working with additional blocks and features.";
            const finalMessage =
              isDirty || hasContent
                ? `${baseMessage}\n\nOpening this file will replace your current workspace content. Any unsaved changes will be cleared. Do you want to proceed?`
                : `${baseMessage} Do you want to proceed?`;

            openDialog({
              type: "LOAD_LOCAL_PROJECT",
              title: "Open in Junior Advanced?",
              message: finalMessage,
              confirmText: "Open Project",
              confirmClass:
                "bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white",
              cancelText: "Cancel",
              showInput: false,
              payload: localProject,
            });
            e.target.value = null;
            return;
          }

          // Compatibility Rule 5: Same environment match (Basic in Basic, Advanced in Advanced, Scratch in Scratch)
          const targetEnv = isJuniorWorkspace ? activeWorkspaceEnv : "scratch";
          const localProject = {
            projectName: newName,
            environment: targetEnv,
            code: arrayBuffer,
          };
          promptOrLoad(localProject, targetEnv, getEnvDisplayName(targetEnv));
        } else {
          toast.error("Please upload a valid .zip, .py, .js, or .sb3 file.");
        }
      } catch (err) {
        console.error("Error reading uploaded file:", err);
        toast.error("Failed to read the uploaded project file.");
      }

      e.target.value = null; // Reset input for repeated selections
    },
    [
      activeWorkspaceEnv,
      activeWorkspaceCode,
      isDirty,
      openDialog,
      executeLoadLocalProject,
    ]
  );

  return {
    fileInputRef,
    handleUploadLocalClick,
    handleFileChange,
    handleDownloadClick,
    executeDownloadLocal,
    executeLoadLocalProject,
  };
};

export default useWorkspaceFileIO;
